import React from 'react';
import type { ReactNode } from 'react';
import { DeleteOutlined, GroupOutlined } from '@ant-design/icons';
import { Button, InputNumber, Typography, Tag } from 'antd';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

interface BundleLike {
  sku: string;
  name: string;
}

interface BundleItemGroupProps {
  bundle: BundleLike;
  setQuantity: number;
  setQuantityMin?: number;
  onSetQuantityChange: (value: number | null) => void;
  onRemove: () => void;
  bundleShippedSets?: number;
  bundleHasPartialShipped?: boolean;
  children?: ReactNode;
}

/**
 * 組合商品群組外框
 *
 * 包裹一組由 bundle 展開的 ProductItemCard，提供：
 * - 組合標題列 (bundle name / sku)
 * - 組數控制器 (set quantity)
 * - 整組刪除按鈕
 *
 * 子項目 (ProductItemCard) 由外部 render，本元件只負責外框。
 *
 * 統一規則：「異動後 order_items 必須完整涵蓋 shipped」
 * - setQuantity InputNumber 仍可調整，min = setQuantityMin（涵蓋已出貨所需的最小套數）
 *     超過 min 即可降低／提高，不做硬鎖
 * - 任一 component 已出貨 → 隱藏整組刪除按鈕（避免一次刪除帶走已出貨 row）
 * - 標題列徽章（資訊 only）：
 *     - bundleShippedSets > 0           → 「已出 X 套」
 *     - bundleHasPartialShipped (set=0) → 「部分出貨」（湊不滿一整套，但 component
 *       層級各自顯示自己的「已出 X/Y」）
 */
const BundleItemGroup: React.FC<BundleItemGroupProps> = ({
  bundle,
  setQuantity,
  setQuantityMin = 1,
  onSetQuantityChange,
  onRemove,
  bundleShippedSets = 0,
  bundleHasPartialShipped = false,
  children,
}) => {
  const { t } = useTranslation();
  const hasAnyShipped = bundleShippedSets > 0 || bundleHasPartialShipped;

  return (
    <div className="bundle-item-group" style={{ marginBottom: 24 }}>
      {/* Header bar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '8px 12px',
          marginBottom: 2,
          background: '#f0f5ff',
          borderBottom: '2px solid #1677ff',
        }}
      >
        <GroupOutlined style={{ color: '#1677ff', fontSize: 14 }} />
        <Text strong style={{ fontSize: 13, color: '#1d2129', flex: 1 }}>
          {bundle.sku}
          <Text type="secondary" style={{ fontWeight: 'normal', marginLeft: 8 }}>
            {bundle.name}
          </Text>
        </Text>

        {bundleShippedSets > 0 && (
          <Tag color="blue">
            {t('pages.orders.edit.row_shipped_bundle_badge', { shipped: bundleShippedSets })}
          </Tag>
        )}
        {bundleShippedSets === 0 && bundleHasPartialShipped && (
          <Tag color="orange">
            {t('pages.orders.edit.row_shipped_bundle_partial_badge')}
          </Tag>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t('shared.orders.form.bundle.set_quantity')}
          </Text>
          <InputNumber
            min={setQuantityMin}
            size="small"
            value={setQuantity}
            onChange={onSetQuantityChange}
            style={{ width: 72 }}
          />
        </div>

        {/* 整組刪除：任一 component 已出貨時隱藏（後端 CANNOT_REMOVE 會擋；前端先預防） */}
        {!hasAnyShipped && (
          <Button
            type="text"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={onRemove}
          />
        )}
      </div>

      {/* Child ProductItemCards */}
      <div style={{ paddingLeft: 12 }}>
        {children}
      </div>
    </div>
  );
};

export default BundleItemGroup;
